import { toWSDateString, WSPillProps } from "@wingspanhq/fe-component-library";
import {
  PayableRow,
  PayableStatus,
  PayeeReviewStatus,
  PayerApprovalStatus
} from "@wingspanhq/wingspan-api-sdk";

// Result shape consumed by WSTableCell's `pill` + `secondaryText` props.
export type PayableStatusCellProps = {
  pill: WSPillProps;
  secondaryText?: string;
};

// Pill presets — mirror the legacy `mapStatusProjection`, driven by the v3
// `PayableRow` `status` + `payerApprovalStatus` / `payeeReviewStatus` refinements.
const PILL_DRAFT: WSPillProps = {
  theme: "neutral",
  icon: "draft",
  text: "Draft"
};
const PILL_CANCELLED: WSPillProps = {
  theme: "neutral",
  icon: "draft",
  text: "Cancelled"
};
const PILL_ACTION_REQUIRED: WSPillProps = {
  theme: "warning",
  icon: true,
  text: "Action required"
};
const PILL_AWAITING_CONTRACTOR: WSPillProps = {
  theme: "neutral",
  icon: "time",
  text: "Awaiting contractor"
};
const PILL_SCHEDULED: WSPillProps = {
  theme: "blue",
  icon: "calendar",
  text: "Scheduled"
};
const PILL_PAID: WSPillProps = {
  theme: "success",
  icon: true,
  text: "Paid"
};
const PILL_OFF_PLATFORM: WSPillProps = {
  theme: "neutral",
  icon: "paperclip",
  text: "Off-platform"
};
const PILL_IN_TRANSIT: WSPillProps = PILL_PAID;
const PILL_REFUNDED: WSPillProps = {
  theme: "neutral",
  text: "Refunded"
};
const PILL_RETURNED: WSPillProps = {
  theme: "error",
  icon: true,
  text: "Returned"
};
// Every member of `PayableStatus` is mapped below, so the two fallbacks are
// distinct failure modes and read differently to the user (PLA-3538):
// - the row came back without a status — unavailable, not unknown.
// - a value outside the enum the app was built against.
const PILL_STATUS_UNAVAILABLE: WSPillProps = {
  theme: "neutral",
  icon: "info-circle",
  text: "Status unavailable"
};
const PILL_UNRECOGNIZED: WSPillProps = {
  theme: "warning",
  icon: true,
  text: "Unrecognized status"
};

const formatMonthDate = (value: Date | undefined): string =>
  toWSDateString(value, "monthDate");

/**
 * Derive the payer-facing status pill + secondary text from a v3 `PayableRow`.
 *
 * Accepted approximation vs. the v1 `Payable` (recorded in
 * V3_MIGRATION_TRACKER.md): `PayableRowEvents` carries no dispute/resubmit
 * timestamps (only the full `Payable` does), so the dated dispute/resubmit
 * copy collapses onto the current `payeeReviewStatus` alone.
 */
export const getPayableStatusCellProps = (
  payable: PayableRow
): PayableStatusCellProps => {
  const status = payable.status;
  const events = payable.events;
  const approvalStatus = payable.payerApprovalStatus;

  if (status === PayableStatus.CREATED) {
    return {
      pill: PILL_DRAFT,
      secondaryText: events?.createdAt
        ? `Created • ${formatMonthDate(events.createdAt)}`
        : undefined
    };
  }

  if (status === PayableStatus.CANCELLED) {
    return { pill: PILL_CANCELLED };
  }

  if (status === PayableStatus.PAID_OFF_PLATFORM) {
    return { pill: PILL_OFF_PLATFORM, secondaryText: "Historical records" };
  }

  if (
    status === PayableStatus.PAID ||
    status === PayableStatus.PARTIALLY_PAID
  ) {
    return {
      pill: PILL_PAID,
      secondaryText: events?.paidAt
        ? `Payout cleared • ${formatMonthDate(events.paidAt)}`
        : undefined
    };
  }

  if (status === PayableStatus.PAYMENT_IN_TRANSIT) {
    return { pill: PILL_IN_TRANSIT, secondaryText: "Payment in route" };
  }

  if (status === PayableStatus.RETURNED) {
    return { pill: PILL_RETURNED, secondaryText: "Payout could not be sent" };
  }

  if (
    status === PayableStatus.REFUNDED ||
    status === PayableStatus.PARTIALLY_REFUNDED
  ) {
    return { pill: PILL_REFUNDED };
  }

  if (payable.payeeReviewStatus === PayeeReviewStatus.DISPUTED) {
    return {
      pill: PILL_ACTION_REQUIRED,
      secondaryText: "Disputed by contractor"
    };
  }

  if (payable.payeeReviewStatus === PayeeReviewStatus.RESUBMITTED) {
    return {
      pill: PILL_ACTION_REQUIRED,
      secondaryText: "Contractor resubmitted"
    };
  }

  if (status === PayableStatus.OPENED) {
    if (approvalStatus === PayerApprovalStatus.APPROVED) {
      return { pill: PILL_SCHEDULED, secondaryText: "Future payroll" };
    }
    return { pill: PILL_ACTION_REQUIRED, secondaryText: "Approval by you" };
  }

  if (status === PayableStatus.PENDING) {
    if (approvalStatus === PayerApprovalStatus.APPROVED) {
      return {
        pill: PILL_SCHEDULED,
        secondaryText: "To be funded • Future payroll"
      };
    }
    return {
      pill: PILL_AWAITING_CONTRACTOR,
      secondaryText: "Eligibility pending"
    };
  }

  return { pill: status ? PILL_UNRECOGNIZED : PILL_STATUS_UNAVAILABLE };
};
